/**
 * GA4 user properties (GROWTH-8133).
 *
 * The dashboard bootstrap carries `ga_user_properties`, which the server defines once for every sender
 * (`Hw_Listing_GaUserProperties`, listings/library/Hw/Listing/GaUserProperties.php): `member_type`, `plan` and
 * `primary_profession`. `setGaUserProperties` hands them to gtag, so every later hit carries them and GA's user-scoped
 * custom dimensions can split users, retention and funnels by them. The values are fixed words or a profession title,
 * never personal data; each is checked again here, and a value that fails is sent as `null`.
 *
 * `gtag("set", …)` as for the User-ID (helpers/gaUserId.js). Only the production build defines `window.gtag`
 * (helpers/analyticsTags.js); elsewhere both functions do nothing. See docs/testing/inventories/gaUserProperties.md.
 */

const MEMBER_TYPES = ["listing_owner", "consumer"];
const PLANS = ["sponsor", "free"];
// GA4 drops user property values past 36 characters.
const VALUE_MAX_LENGTH = 36;

const isOneOf = words => value => words.includes(value);

// A catalogue title such as "Lawyers": never an email or a phone number.
const isProfessionTitle = value =>
  typeof value === "string" && value.trim() !== "" && value.length <= VALUE_MAX_LENGTH && !/[0-9@]/.test(value);

// Each property the dashboard sends, with the check its value must pass.
const PROPERTY_CHECKS = {
  member_type: isOneOf(MEMBER_TYPES),
  plan: isOneOf(PLANS),
  primary_profession: isProfessionTitle
};

const PROPERTY_NAMES = Object.keys(PROPERTY_CHECKS);

// What gtag was last given in this page session, so a repeated bootstrap does not set the same values again.
let sentProperties = null;

/** Every known property, its value or `null`; `null` overall when no value is valid. */
export const gaUserPropertiesFor = profile => {
  const source = profile?.ga_user_properties;

  if (!source || typeof source !== "object") {
    return null;
  }

  const properties = {};
  let validCount = 0;

  PROPERTY_NAMES.forEach(name => {
    const value = source[name];

    if (PROPERTY_CHECKS[name](value)) {
      properties[name] = value;
      validCount += 1;
    } else {
      properties[name] = null;
    }
  });

  return validCount > 0 ? properties : null;
};

export const setGaUserProperties = profile => {
  const properties = gaUserPropertiesFor(profile);

  if (!properties || typeof window.gtag !== "function") {
    return false;
  }

  const key = JSON.stringify(properties);
  if (key === sentProperties) {
    return false;
  }

  window.gtag("set", "user_properties", properties);
  sentProperties = key;

  return true;
};

/**
 * Called before every Keycloak sign-out (helpers/keycloakLogout.js), so a shared computer does not carry one member's
 * properties into the next session. Only what this page set is cleared.
 */
export const clearGaUserProperties = () => {
  const wasSet = sentProperties !== null;
  sentProperties = null;

  if (wasSet && typeof window.gtag === "function") {
    window.gtag("set", "user_properties", Object.fromEntries(PROPERTY_NAMES.map(name => [name, null])));
  }
};
